<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
defineProps({
  backgroundColor: {
    type: String,
    default: () => '',
  },
  title: {
    type: String,
    default: () => '',
  },
  description: {
    type: String,
    default: () => '',
  },
  address: {
    type: String,
    default: () => '',
  },
  phone: {
    type: Array,
    default: () => [],
  },
  mail: {
    type: Array,
    default: () => [],
  },
  website: {
    type: String,
    default: () => '',
  },
  time: {
    type: String,
    default: () => '',
  },
  caption: {
    type: String,
    default: () => '',
  },
  template: {
    type: String,
    default: () => '',
  },
});
</script>
<template>
  <div
    :class="[
      'contacts-card',
      `contacts-card--${template}`,
      backgroundColor ? `contacts-card--${backgroundColor}` : '',
    ]"
  >
    <div class="contacts-card__top">
      <h2
        v-if="template === 'contacts'"
        class="contacts-card__title m-0 h2"
        v-html="title"
      ></h2>
      <p v-else class="contacts-card__title m-0 h4" v-html="title"></p>
      <div
        v-if="description"
        class="contacts-card__description body-1-regular"
        v-html="description"
      ></div>
      <div
        v-if="address || phone?.length > 0 || mail?.length > 0"
        :class="[
          'contacts-card__data',
          template === 'contacts' ? 'body-1-regular' : 'body-3-regular',
        ]"
      >
        <div
          v-if="address"
          :class="[
            'contacts-card__address',
            template === 'contacts' ? 'body-1-bold' : '',
          ]"
          v-html="address"
        ></div>
        <ul
          v-if="phone?.length > 0 || mail?.length > 0 || website?.length > 0"
          class="m-0 p-0 contacts-card__contacts"
        >
          <template v-if="phone?.length > 0">
            <li
              v-for="(ph, idx) in phone"
              :key="idx"
              :class="[
                'contacts-card__contact',
                template === 'contacts' ? 'body-1-bold' : '',
              ]"
            >
              <a :href="`tel:${formatTelLink(ph)}`">
                {{ ph }}
              </a>
            </li>
          </template>
          <template v-if="mail?.length > 0">
            <li
              :class="[
                'contacts-card__contact',
                template === 'contacts' ? 'body-1-bold' : '',
              ]"
            >
              <template v-for="(mailItem, mailIndex) in mail" :key="mailIndex">
                <template v-if="mailIndex >= 1">, </template>
                <a :href="`mailto:${mailItem}`" target="_blank">
                  {{ mailItem }}
                </a>
              </template>
            </li>
          </template>
          <template v-if="website?.length > 0">
            <li
              :class="[
                'contacts-card__contact',
                template === 'contacts' ? 'body-1-bold' : '',
              ]"
            >
              <a :href="website" target="_blank">
                {{ website }}
              </a>
            </li>
          </template>
          <template v-if="time.length > 0">
            <li :class="['contacts-card__contact']" v-html="time"></li>
          </template>
        </ul>
      </div>
    </div>
    <div
      v-if="caption"
      class="contacts-card__caption body-3-bold"
      v-html="caption"
    ></div>
  </div>
</template>
<style scoped lang="scss">
@use 'sass:math';
.contacts-card {
  $parent: &;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  padding: 24px;
  @include media-breakpoint-up(sm) {
    gap: 40px;
    padding: 32px;
  }
  @include media-breakpoint-up(md) {
    gap: 40px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(gap, 40);
    @include responsive-spacing(padding, 32);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
  }
  &__description {
    color: $on-surface-variant-2;
  }
  &__data {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__address {
    color: $on-surface-variant-2;
  }
  &__contacts {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    padding: 0;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__contact {
    margin: 0;
  }
  &__caption {
    margin-top: auto;
  }
  &--contacts {
    @include media-breakpoint-up(sm) {
      padding: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 40);
    }
    #{$parent} {
      &__top {
        height: 100%;
      }
      &__contacts {
        column-gap: 32px;
        row-gap: 8px;
        @include media-breakpoint-up(sm) {
          flex-direction: row;
          flex-wrap: wrap;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(column-gap, 32);
          @include responsive-spacing(row-gap, 8);
        }
      }
      &__address {
        color: $black;
      }
      &__description {
        @include media-breakpoint-up(sm) {
          margin-bottom: 16px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(margin-bottom, 16);
        }
      }
      &__data {
        gap: 32px;
        @include media-breakpoint-up(sm) {
          gap: 48px;
        }
        @include media-breakpoint-up(md) {
          margin-top: auto;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 48);
        }
      }
    }
  }
  &--white {
    background-color: $gray;
  }
  &--black {
    background-color: rgba($surface-container-opacity, 0.5);
  }
  &--gray {
    background-color: $white;
  }
}
</style>
